import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { 
  CareersSection, 
  UnifiedFooter,
  SectionHeading
} from '../components';
import { Nav } from '../components/Nav';
import { motion } from 'framer-motion';
import '../styles/global.css';

const CareersPage: React.FC = () => {
  const [hovered, setHovered] = useState<string | null>(null);

  const careerPositions = [
    { 
      title: 'Research Engineer',
      location: 'New York'
    },
    { 
      title: 'Product Engineer',
      location: 'New York'
    },
    { 
      title: 'Anything else',
      location: 'New York'
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  // Prevent flash of unstyled content
  useEffect(() => {
    document.body.style.backgroundColor = '#ffffff';
    document.body.style.color = '#000000';
    document.body.style.margin = '0';

    // Set SEO title and meta
    document.title = 'Careers | Syntra – Detect social engineering, verify requests';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Syntra helps IT helpdesks protect against social engineering and deepfakes with detection and verification tools..');
    }
    
    return () => {
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
      document.body.style.margin = '';
    };
  }, []);

  return (
    <motion.div 
      className="min-h-screen bg-white text-dark-900 font-sans overflow-x-clip"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      variants={containerVariants}
    >
      {/* Navigation */}
      <Nav activeLink="careers" />

      {/* Hero Section */}
      <motion.section 
        className="hero-section pt-40 sm:pt-48 relative bg-gradient-to-b from-gray-50 to-white overflow-hidden"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="page-padding">
          <div className="container-medium">
              <motion.div className="max-w-3xl" variants={itemVariants}>
                <SectionHeading
                  caption="CAREERS"
                  title="Help us secure the future of human verification."
                  description=""
                  className="text-left"
                />
              </motion.div>
          </div>
        </div>
        <div className="page-padding">
          <div className="container-medium">
              <div className="prose prose-lg max-w-3xl">
                <motion.p className="text-lg mb-6" variants={itemVariants}>
                  Syntra is building the future of human verification - making sure the person on the other end is who they say they are, especially when it matters most.
                </motion.p>
                <motion.p className="text-lg mb-6" variants={itemVariants}>
                  We start with IT, where the risk from deepfakes, vishing, and account takeovers is most acute. But our mission is broader: to protect critical workflows everywhere humans and AI interact.
                </motion.p>
                <motion.p className="text-lg mb-6" variants={itemVariants}>
                  We're a small, engineering-first team with roots in security, infrastructure, and machine learning. If you want to help shape the future of human verification, let's talk.
                </motion.p>
                
                <motion.p className="text-lg font-medium mb-12" variants={itemVariants}>
                  - <a className="underline" href="https://www.linkedin.com/in/patilneil/">Neil</a>, <a className="underline" href="https://www.linkedin.com/in/utsav2/">Utsav</a>, and the growing Syntra team
                </motion.p>
            </div>
          </div>
        </div>

      </motion.section>

      {/* Main Content */}
      <motion.section className="py-16 bg-white" variants={itemVariants}>
        <div className="page-padding">
          <div className="container-medium">
            <div className="padding-large">
              <SectionHeading
                caption="OPEN POSITIONS"
                title=""
                description=""
                className="text-left"
              />
              
              <div className="mt-12 grid gap-6">
                {careerPositions.map((position, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                    variants={itemVariants}
                  >
                    <Link 
                      to={
                        position.title === 'Research Engineer' 
                          ? '/careers/research-engineer' 
                          : position.title === 'Product Engineer'
                            ? '/careers/product-engineer'
                            : '/careers/anything-else'
                      }
                      className="block no-underline"
                    >
                      <div 
                        className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-300"
                      >
                        <div className="p-6 flex justify-between items-center">
                          <div>
                            <h3 className="text-xl font-semibold text-dark-900 mb-1">{position.title}</h3>
                            <p className="text-dark-600">{position.location}</p>
                          </div>
                          <div className="flex items-center text-blue-600">
                            <span className="mr-2 font-medium">View Position</span>
                            <ChevronRight className="h-5 w-5" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* Values Section */}
      {/*<motion.section className="py-16 bg-white" variants={itemVariants}>
        <div className="page-padding">
          <div className="container-medium">
            <div className="padding-vertical padding-large">
              <SectionHeading
                caption="OUR VALUES"
                title="Operating Principles"
                description="These principles guide how we work and make decisions every day."
                className="text-center"
              />
              
              <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
                <motion.div 
                  className="p-6 bg-gray-50 rounded-lg border border-gray-100"
                  variants={itemVariants}
                >
                  <h3 className="text-xl font-semibold mb-3">Move with conviction</h3>
                  <p className="text-dark-700">Figure out what data you need, then bias to action.</p>
                </motion.div>
                
                <motion.div 
                  className="p-6 bg-gray-50 rounded-lg border border-gray-100"
                  variants={itemVariants}
                >
                  <h3 className="text-xl font-semibold mb-3">Debug your own thinking</h3>
                  <p className="text-dark-700">Be intentional and clear about your reasoning. Frameworks matter.</p>
                </motion.div>
                
                <motion.div 
                  className="p-6 bg-gray-50 rounded-lg border border-gray-100"
                  variants={itemVariants}
                >
                  <h3 className="text-xl font-semibold mb-3">Spend innovation tokens wisely</h3>
                  <p className="text-dark-700">Don't reinvent wheels. Focus energy where it matters most.</p>
                </motion.div>
                
                <motion.div 
                  className="p-6 bg-gray-50 rounded-lg border border-gray-100"
                  variants={itemVariants}
                >
                  <h3 className="text-xl font-semibold mb-3">Operate with integrity</h3>
                  <p className="text-dark-700">Trust is everything. Do the right thing for customers and partners.</p>
                </motion.div>
              </div>
              
              <motion.p className="text-lg text-center mt-12 max-w-3xl mx-auto" variants={itemVariants}>
                Our customers are the builders of the future. They're increasingly diverse, and we won't be able to support them well unless our team reflects that diversity. Whether you're new to security, have taken an unconventional career path, or come from an underrepresented background, we want to hear from you.
              </motion.p>
            </div>
          </div>
        </div>
      </motion.section>*/}
      
      {/* Footer */}
      <UnifiedFooter itemVariants={itemVariants} />
    </motion.div>
  );
};

export default CareersPage;
